/**
 * Repeat-group collapse / expand helpers for the segment editor.
 *
 * Segments in the DB are stored EXPANDED — one row per lap per slot — with
 * repeat_group_id / repeat_lap_index / slot_in_lap metadata.  The editor can
 * display them either expanded (individual mode) or collapsed into groups
 * (group mode).  These two functions convert between the two representations.
 */

/**
 * @typedef {{ type: 'single', segment: object }} SingleItem
 * @typedef {{ type: 'group', groupId: number, cycles: number, template: object[] }} GroupItem
 * @typedef {SingleItem | GroupItem} EditorItem
 */

/**
 * Collapse expanded segment rows into a mixed list of singles and groups.
 *
 * Walks the rows in sort order.  Consecutive rows sharing the same non-null
 * repeat_group_id are collapsed into one GroupItem whose `template` contains
 * the segments from lap 0, and `cycles` is the number of distinct laps.
 *
 * @param {object[]} rows  Flat segment rows (from API / DB).
 * @returns {EditorItem[]}
 */
export function collapseSegmentRowsToGroups(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];

  const items = [];
  let i = 0;

  while (i < rows.length) {
    const r = rows[i];
    const gid = r.repeat_group_id != null ? Number(r.repeat_group_id) : null;

    if (gid == null || !Number.isFinite(gid)) {
      items.push({ type: 'single', segment: { ...r } });
      i += 1;
      continue;
    }

    const blockRows = [];
    while (i < rows.length) {
      const rr = rows[i];
      const g2 = rr.repeat_group_id != null ? Number(rr.repeat_group_id) : null;
      if (g2 !== gid) break;
      blockRows.push(rr);
      i += 1;
    }

    const maxLap = Math.max(0, ...blockRows.map((x) => Number(x.repeat_lap_index) || 0));
    const cycles = maxLap + 1;

    const templateRows = blockRows
      .filter((x) => (Number(x.repeat_lap_index) || 0) === 0)
      .sort((a, b) => (Number(a.slot_in_lap) || 0) - (Number(b.slot_in_lap) || 0));

    const template = templateRows.map((tr) => {
      const s = { ...tr };
      delete s.repeat_group_id;
      delete s.repeat_lap_index;
      delete s.slot_in_lap;
      return s;
    });

    if (template.length > 0) {
      items.push({ type: 'group', groupId: gid, cycles, template });
    }
  }

  return items;
}

/**
 * Expand a mixed list of singles / groups back to flat segment rows with
 * correct repeat metadata, ready for the API.
 *
 * @param {EditorItem[]} items
 * @returns {object[]}  Flat segment rows.
 */
export function expandGroupsToSegmentRows(items) {
  if (!Array.isArray(items)) return [];

  const rows = [];
  let nextGroupId = 1;

  for (const item of items) {
    if (item.type === 'single') {
      const seg = { ...item.segment };
      seg.repeat_group_id = null;
      seg.repeat_lap_index = null;
      seg.slot_in_lap = null;
      rows.push(seg);
    } else if (item.type === 'group') {
      const gid = nextGroupId++;
      const cycles = Math.max(1, item.cycles || 1);
      for (let lap = 0; lap < cycles; lap++) {
        let slot = 0;
        for (const tmpl of item.template) {
          rows.push({
            ...tmpl,
            repeat_group_id: gid,
            repeat_lap_index: lap,
            slot_in_lap: slot++,
          });
        }
      }
    }
  }

  return rows;
}

/**
 * Check whether any row has repeat group metadata.
 * @param {object[]} rows
 * @returns {boolean}
 */
export function hasRepeatGroups(rows) {
  if (!Array.isArray(rows)) return false;
  return rows.some((r) => r.repeat_group_id != null && Number.isFinite(Number(r.repeat_group_id)));
}
